
import { motion } from "framer-motion";
import AppointmentsScreen from "../../components/dashboard/AppointmentsScreen";
import PaymentCompletion from "../../components/dashboard/PaymentCompletion";
import PatientWelcomeSection from "../../components/dashboard/PatientWelcomeSection";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { getPatientById } from "../../redux/patientSlice";
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { useTranslation } from 'react-i18next';
import { useDashboardSocket } from '../../hooks/useDashboardSocket';

// Booking-analytics range (Week/Month/Year) → ISO start/end. Deterministic within
// a period so the socket params stay stable across renders.
const getBookingRange = (range) => {
  const today = new Date();
  let startDate, endDate;
  switch (range) {
    case 'Month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); endDate.setHours(23, 59, 59, 999);
      break;
    case 'Year':
      startDate = new Date(today.getFullYear(), 0, 1); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today.getFullYear(), 11, 31); endDate.setHours(23, 59, 59, 999);
      break;
    case 'Week':
    default: {
      const currentDay = today.getDay();
      const first = new Date(today);
      first.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));
      first.setHours(0, 0, 0, 0);
      const last = new Date(first);
      last.setDate(first.getDate() + 6);
      last.setHours(23, 59, 59, 999);
      startDate = first; endDate = last;
    }
  }
  return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
};

function PatientDashboard() {
    const { t } = useTranslation('dashboard');
    const patientId = localStorage.getItem("ID");
    const userRole = localStorage.getItem("Role");
    const dispatch = useDispatch();
    const isInitialCall = useRef(true);
    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);

    // Booking-analytics range (lifted out of PatientWelcomeSection).
    const [bookingRange, setBookingRange] = useState('Week');
    const bookingDates = getBookingRange(bookingRange);

    const dashboardParams = {
      patId: patientId,
      bookingStartDate: bookingDates.startDate,
      bookingEndDate: bookingDates.endDate,
    };
    const { data: dashboardData, loading: dashboardLoading } =
      useDashboardSocket('patient', dashboardParams, { enabled: userRole === 'patient' });


  useEffect(() => {
    if (isInitialCall.current) {
      if (userRole === 'patient' && patientId) {
        dispatch(getPatientById(patientId));
      }
      isInitialCall.current = false;
    }
  }, [dispatch, userRole]);

  // Handle loading state
  if (isLoading) {
    return (
      <div data-testid="patient-dashboard-loading" className="bg-white rounded-2xl p-6 w-full h-full flex items-center justify-center shadow-lg">
        <div className="flex flex-col items-center justify-center gap-4 py-6">
          <CommonLoader />
          <span data-testid="patient-dashboard-loading-text" className="text-xl text-[#4ABDBD] font-semibold">{t('dashboard.loading')}</span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    if (error?.status >= 500 && error?.status <= 599) {
      return <ErrorPage error={error} pageName={t('dashboard.patient_dashboard')} />;
    }
    if (error?.message === 'Network Error') {
      return <ErrorPage error={error} pageName={t('dashboard.patient_dashboard')} />;
    }
    return <ErrorPage error={error} pageName={t('dashboard.patient_dashboard')} />;
  }

  if (!selectedPatient) {
    return (
      <ErrorPage error={{ message: t('dashboard.no_patient_data') }} pageName={t('dashboard.patient_dashboard')} />
    );
  }
  
  return (
    <motion.div
      data-testid="patient-dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`${window.innerWidth <= 1024 ? 'p-4' : 'p-0'}`}
    >
      {/* Dashboard Content Container */}
      <div data-testid="patient-dashboard-content" className="grid gap-4">
        {/* Welcome Section */}
        <PatientWelcomeSection
          booking={dashboardData?.booking}
          loading={dashboardLoading}
          selectedRange={bookingRange}
          onRangeChange={setBookingRange}
        />

        <div data-testid="patient-dashboard-bottom-row" className="flex flex-col xl:flex-row w-full gap-4 ">
          <div data-testid="patient-dashboard-appointments-column" className="w-full xl:w-1/2">
            <AppointmentsScreen
              appointmentsUpcoming={dashboardData?.appointmentsUpcoming}
              appointmentsPast={dashboardData?.appointmentsPast}
              loading={dashboardLoading}
            />
          </div>
          <div data-testid="patient-dashboard-payments-column" className="w-full xl:w-1/2">
            <PaymentCompletion
              payments={dashboardData?.payments}
              loading={dashboardLoading}
            />
          </div>
        </div>
      </div>
    </motion.div>

  );
}

export default PatientDashboard;
